import React, { useState } from "react";
import {
  Play,
  Pause,
  Square,
  Plus,
  Minus,
  Zap,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface ExhibitionQuickActionsProps {
  eventId: string | null;
  eventState: string;
  isAdminOverride: boolean;
  isPaused: boolean;
  onStateChange: () => void;
}

const ExhibitionQuickActions: React.FC<ExhibitionQuickActionsProps> = ({
  eventId,
  eventState,
  isAdminOverride,
  isPaused,
  onStateChange,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [actionReason, setActionReason] = useState("");

  const handleToggleAdminOverride = async (enabled: boolean) => {
    if (!eventId) {
      toast.error("No event to modify");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("set_exhibition_admin_override", {
        p_event_id: eventId,
        p_enabled: enabled,
        p_reason: actionReason || `Admin override ${enabled ? "enabled" : "disabled"}`,
      });

      if (error) throw error;

      toast.success(enabled ? "Event is now LIVE" : "Event returned to schedule");
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error toggling admin override:", error);
      toast.error("Failed to toggle admin override");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePause = async (paused: boolean) => {
    if (!eventId) {
      toast.error("No event to modify");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("set_exhibition_paused", {
        p_event_id: eventId,
        p_paused: paused,
        p_reason: actionReason || `Event ${paused ? "paused" : "unpaused"}`,
      });

      if (error) throw error;

      toast.success(paused ? "Event paused" : "Event resumed");
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error toggling pause:", error);
      toast.error("Failed to toggle pause");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseEvent = async () => {
    if (!eventId) {
      toast.error("No event to close");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("close_exhibition_event", {
        p_event_id: eventId,
        p_reason: actionReason || "Event closed by admin",
      });

      if (error) throw error;

      toast.success("Event closed");
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error closing event:", error);
      toast.error("Failed to close event");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdjustTime = async (minutes: number) => {
    if (!eventId) {
      toast.error("No event to modify");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.rpc("adjust_exhibition_end_time", {
        p_event_id: eventId,
        p_minutes_delta: minutes,
        p_reason: actionReason || `Time adjusted by ${minutes} minutes`,
      });

      if (error) throw error;

      toast.success(`Event ${minutes > 0 ? "extended" : "reduced"} by ${Math.abs(minutes)} minutes`);
      setActionReason("");
      onStateChange();
    } catch (error) {
      console.error("Error adjusting time:", error);
      toast.error("Failed to adjust time");
    } finally {
      setIsLoading(false);
    }
  };

  if (!eventId) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        <p className="text-sm">No active event. Create one above to enable quick actions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Force Live Toggle */}
      <div
        className={cn(
          "flex items-center justify-between p-4 rounded-xl border transition-colors",
          isAdminOverride
            ? "bg-amber-500/10 border-amber-500/30"
            : "bg-muted/30 border-muted"
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            isAdminOverride ? "bg-amber-500/20" : "bg-muted"
          )}>
            <Zap className={cn(
              "w-5 h-5",
              isAdminOverride ? "text-amber-500" : "text-muted-foreground"
            )} />
          </div>
          <div>
            <p className="font-medium">Force Live</p>
            <p className="text-xs text-muted-foreground">
              {isAdminOverride ? "Event is live regardless of schedule" : "Follow scheduled times"}
            </p>
          </div>
        </div>
        <Switch
          checked={isAdminOverride}
          onCheckedChange={handleToggleAdminOverride}
          disabled={isLoading}
        />
      </div>

      {/* State Control Buttons */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase">State Control</Label>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={eventState === "live" && !isPaused ? "default" : "outline"}
            onClick={() => handleToggleAdminOverride(true)}
            disabled={isLoading || (eventState === "live" && isAdminOverride)}
            className="flex-1 gap-1"
          >
            <Play className="w-4 h-4" />
            Open
          </Button>
          <Button
            size="sm"
            variant={isPaused ? "default" : "outline"}
            onClick={() => handleTogglePause(!isPaused)}
            disabled={isLoading}
            className="flex-1 gap-1"
          >
            <Pause className="w-4 h-4" />
            {isPaused ? "Resume" : "Pause"}
          </Button>
          <Button
            size="sm"
            variant={eventState === "closed" ? "destructive" : "outline"}
            onClick={handleCloseEvent}
            disabled={isLoading || eventState === "closed"}
            className="flex-1 gap-1"
          >
            <Square className="w-4 h-4" />
            Close
          </Button>
        </div>
      </div>

      {/* Time Adjustments */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase">Adjust Time</Label>
        <div className="grid grid-cols-4 gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAdjustTime(-30)}
            disabled={isLoading}
            className="text-xs"
          >
            <Minus className="w-3 h-3 mr-1" />30m
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAdjustTime(-15)}
            disabled={isLoading}
            className="text-xs"
          >
            <Minus className="w-3 h-3 mr-1" />15m
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAdjustTime(15)}
            disabled={isLoading}
            className="text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />15m
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAdjustTime(30)}
            disabled={isLoading}
            className="text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />30m
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAdjustTime(60)}
            disabled={isLoading}
            className="text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />1 hour
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleAdjustTime(-60)}
            disabled={isLoading}
            className="text-xs"
          >
            <Minus className="w-3 h-3 mr-1" />1 hour
          </Button>
        </div>
      </div>

      {/* Action Reason */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase">Reason (optional)</Label>
        <Input
          value={actionReason}
          onChange={(e) => setActionReason(e.target.value)}
          placeholder="Reason for action..."
          className="text-sm"
        />
      </div>

      {/* Warnings */}
      {isAdminOverride && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Admin override active. Event is shown as LIVE regardless of schedule.</span>
        </div>
      )}

      {isPaused && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 text-blue-700 dark:text-blue-400 text-sm">
          <Pause className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Event is paused. Users see the locked state.</span>
        </div>
      )}
    </div>
  );
};

export default ExhibitionQuickActions;